/**
 * The Reddit pixel runtime (ui#834) — React-free, browser-guarded, and the ONLY place this app touches
 * `window.rdt`, injects a Reddit script, or names a Reddit identifier.
 *
 * The consent contract is the same one {@link ./googleTags} states, and it is stricter here: nothing loads
 * until `consent.marketing` is granted. There is no cookieless mode to fall back to and no denied-defaults
 * ping — a visitor who has not decided, or who refused, causes zero requests to `redditstatic.com` and
 * leaves `window.rdt` undefined. That is what the e2e spec asserts, so the promise is provable rather than
 * argued from this comment.
 *
 * SSG-safe: nothing here runs at module scope, and every export no-ops outside a browser.
 *
 * ── The identifier, and why it is sent RAW ───────────────────────────────────────────────────────
 * `rdt('init', ...)` takes advanced-matching keys, and the pixel HASHES them itself before anything
 * leaves the browser. Read out of `https://www.redditstatic.com/ads/pixel.js` (build `rdt_c6181587`,
 * 2026-09-04) rather than recalled:
 *
 *     function hn(n){                          // email normalizer
 *         var e = n.split("@");
 *         if (2 != e.length) return "";
 *         e[0] = sn(e[0], ".").split("+")[0];  // drop every "." then everything from the first "+"
 *         return e.join("@").toLowerCase();
 *     }
 *     function Nn(n,r){
 *         if (Tn(n)) return n.toLowerCase();   // already a 64-char hex digest -> passthrough
 *         var t = jn[r](n);
 *         return t === G ? t : en().stringify(rn()(t));   // SHA-256, hex
 *     }
 *     // init: bi.config = { ..., em: Nn(j.email, "email") }
 *
 * The address goes RAW because the pixel CANONICALISES before hashing, and its rule is not "lowercase
 * and trim": `hn` deletes every dot from the local part and drops everything from the first `+`. A digest
 * we computed here would disagree with the browser for every dotted or plus-addressed account, and
 * nothing anywhere would report an error — the conversion would simply match nobody.
 *
 * `externalId` is sent RAW too, and for a different reason than the address. Its normalizer in that same
 * build is the IDENTITY function:
 *
 *     var pn = function(n){ return n };          // externalId normalizer
 *     // init: bi.config = { ..., external_id: Nn(j.externalId, "externalId"), ... }
 *
 * so passing the account's canonical uuid produces exactly the digest the api computes as
 * `sha256hex(accountId)` (`RedditMatchKeys` in the api repo, which quotes the same source). Hashing it
 * here instead would need WebCrypto, which is async and secure-context-only, on a value Reddit reads on
 * the page's very FIRST event — and `Tn`'s passthrough means a digest would be accepted anyway, so the
 * raw value buys agreement with the server at no cost.
 *
 * The value is the account's own uuid from `/account` (api#1240), never `User.id`, which is the Firebase
 * Auth UID (`auth/firebaseSession.ts`). They are different identifiers: a report keyed on the uid would
 * carry a conversion id Reddit cannot join to the server's row and a digest that matches nobody, and
 * would count every consenting signup TWICE instead of deduplicating with the server's report.
 *
 * No `phoneNumber` (owner, 2026-09-04: "we don't want phoneNumber"), no `idfa`, no `aaid` — this is a
 * web integration only.
 *
 * The CLICK IDENTIFIER is not passed at all: the pixel reads `rdt_cid` from `window.location.search`
 * itself at init and attaches it to every event. That is also why {@link startRedditPixel} must not be
 * deferred past a navigation away from the landing URL.
 */

import {publicLandingPath} from "attribution/adAttribution";
import {isClientOnlyRoutePath} from "clientOnlyRouteShell";
import type {ConsentState} from "consent/consentStore";
import {clearRedditSignUpPending, redditSignUpPending} from "./redditSignUpMark";
import {treeRelativePath} from "seo/documentLang";

/** The advanced-matching keys this integration sends. RAW; the pixel hashes them. See above. */
export interface RedditMatchKeys {
	/** The signed-in account's address, or undefined for a signed-out visitor. */
	email?: string;
	/**
	 * The signed-in account's own uuid (api#1240), or undefined for a signed-out visitor — RAW, because
	 * the pixel's normalizer for this key is the identity function. See the header.
	 */
	externalId?: string;
}

type RdtFunction = ((...args: unknown[]) => void) & {callQueue?: unknown[][]; sendEvent?: unknown};

interface PixelWindow extends Window {
	rdt?: RdtFunction;
}

const PIXEL_SCRIPT_SOURCE = "https://www.redditstatic.com/ads/pixel.js";

/**
 * Whether the pixel may run on this path at all (kitecorp/transferify-ui#2008).
 *
 * WHERE the script runs is the only control we have over two things it does unconditionally:
 *
 * 1. From 2026-09-14 the pixel begins "enhanced metadata sharing" — it reads the page's title,
 *    description and product metadata wherever it runs. The live pixel configuration
 *    (`GET https://pixel-config.reddit.com/pixels/<id>/config`) carries no per-page switch, so the page
 *    must be one whose contents are already public.
 * 2. Every event reports the page address. From the same build the header above quotes
 *    (`rdt_c6181587`): `(t.protocol + "//" + t.hostname + i + yi(t.search)).slice(0, 2048)`, where `i`
 *    is `location.pathname` and `yi` keeps only the parameters in
 *    `["rdt_cid","utm_source","utm_medium","utm_campaign","utm_term","utm_content","utm_name"]`. So the
 *    QUERY string is filtered by Reddit, and the PATHNAME is not: a path that carries a secret hands
 *    that secret to Reddit.
 *
 * Hence the public landing surface and nothing else, asked of the two modules that already own this
 * app's route classification rather than restated here. BOTH are needed, and the second is not
 * belt-and-braces: {@link publicLandingPath} is a DEFAULT-ALLOW grammar — it names eight route families
 * and admits everything else of the right shape — so on its own it would carry the pixel onto the next
 * private route somebody adds, exactly as it already does onto `/personalization`. That list cannot
 * simply grow: it is compared byte for byte against the pattern the api publishes for
 * `AccountAttributionRequest.landingPath`, so this app's privacy scope must not be expressed as an edit
 * to the api's request grammar. {@link isClientOnlyRoutePath} is the other half of the same question and
 * is already maintained as "the private, client-rendered route trees", so a private route added there
 * loses the pixel with no edit here.
 *
 * The three conversion surfaces a future browser-side SignUp/Purchase would want are covered by that
 * same rule for two different reasons, and the split is deliberate: sponsor booking success happens on
 * `/advertise`, which is public and allowed, while signup completion and the hosted-checkout return both
 * happen on `/activate/<token>`, whose PATH carries a single-use token. Those two stay denied, and the
 * denial costs nothing, because BOTH browser conversions work WITH it rather than against it. The
 * `SignUp` (api#1240) is owed by a mark ({@link ./redditSignUpMark}) and spent by
 * {@link trackRedditSignUp} on the first page this rule allows; the {@link trackRedditPurchase} a
 * hosted-checkout return would want is likewise sent on the first allowed page the buyer continues to,
 * which `analytics/usePurchaseConversions` is mounted on the shell to catch. So the token never reaches
 * Reddit, and the server reports both conversions through the Conversions API either way. If an event is
 * ever needed on that return page ITSELF, move the token into the query string, which Reddit filters,
 * rather than widening this rule.
 */
export function redditPixelAllowedOn(pathname: string): boolean {
	// Both halves are asked about the path WITHIN its language tree, because neither can see a locale
	// prefix for itself: `isClientOnlyRoutePath` reads the FIRST segment, which is the prefix rather than
	// the route, and the landing grammar's deny list spells the prefix as a literal `(?:ro/)?` that is
	// byte-locked to the api's published `landingPath` pattern and so cannot be widened here.
	//
	// This used to strip `/ro` alone (ui#2008), which held while that was the only mirror. ui#1498 added
	// the German tree and ui#2471 serves both on the main domain, and `/de/account` then passed BOTH
	// checks — a private route carrying the pixel. Asking `treeRelativePath` instead means a market joins
	// the rule by existing, and a mis-cased prefix collapses the same way React Router matches it, so the
	// scope does not depend on the edge's 301 landing first (kitecorp/devops#353).
	const routePath = treeRelativePath(pathname);
	return publicLandingPath(routePath) && !isClientOnlyRoutePath(routePath);
}

/** The pixel id currently running, or null while nothing is running. Also the single-flight latch. */
let running: string | null = null;

/**
 * The path a view has already been counted for, or null when none has.
 *
 * Here rather than in the hook, because "has this page already been reported" is a rule and rules do not
 * live in React (epic #26). It also makes the answer correct by construction instead of by ordering: the
 * three ways a `PageVisit` leaves this module — `init`'s own, an identify that attaches match keys, and
 * an explicit route change — used to be reconciled by the caller from a boolean snapshot taken before
 * the call, and every ordering the caller did not think of counted the same page twice.
 */
let viewedPath: string | null = null;

/**
 * Whether the pixel is running. Exported so the orchestrator can ask rather than keep a second copy of
 * this latch in step by hand — the pair drifted the moment one of them was cleared and the other was not.
 */
export function redditPixelRunning(): boolean {
	return running !== null;
}

function pixelWindow(): PixelWindow | null {
	return typeof window === "undefined" ? null : (window as PixelWindow);
}

/**
 * The route scope, applied where the pixel is TOUCHED rather than where a caller decides to.
 *
 * Every export that injects the script or hands a command to `rdt` goes through this, so a new caller
 * cannot reach the pixel on a denied page by forgetting to ask. The address is read here rather than
 * passed in for the same reason: an argument is a second copy of the truth, and the pixel itself reads
 * `window.location`, so this is the value it would report.
 *
 * {@link stopRedditPixel} is deliberately NOT gated: a withdrawal must take effect on whatever page the
 * visitor withdrew from, including a denied one.
 */
function allowedHere(scope: PixelWindow): boolean {
	return redditPixelAllowedOn(scope.location.pathname);
}

/**
 * The `rdt` stub, installed exactly as Reddit's snippet documents it.
 *
 * The queue matters: commands issued before `pixel.js` finishes loading are held on `rdt.callQueue` and
 * replayed by the script when it arrives. Without it the `init` that carries the match keys would be lost
 * on every slow connection, and Reddit only reads those on the page's first event.
 */
function rdtOf(scope: PixelWindow): RdtFunction {
	if (!scope.rdt) {
		const queued: RdtFunction = function rdt(...args: unknown[]) {
			if (queued.sendEvent) {
				(queued.sendEvent as (...a: unknown[]) => void)(...args);
			} else {
				queued.callQueue!.push(args);
			}
		} as RdtFunction;
		queued.callQueue = [];
		scope.rdt = queued;
	}
	return scope.rdt;
}

/**
 * Every command handed to `rdt` so far, as plain arrays — the same read
 * {@link ./googleTags.googleTagCommands} offers, and for the same reason: "nothing was sent before
 * consent" is then a fact a test can assert rather than a claim about the source.
 *
 * Reads the queue while the script is still loading and stops reflecting commands once `pixel.js` has
 * taken it over, so a test that asserts on it must assert what was sent EARLY, which is exactly the
 * pre-consent window it exists to police.
 */
export function redditPixelCommands(): unknown[][] {
	return pixelWindow()?.rdt?.callQueue?.map((entry) => Array.from(entry)) ?? [];
}

/**
 * Load and initialise the pixel iff advertising consent is granted and an id is configured.
 *
 * Single-flight: repeated calls (route change, consent change) never re-init and never inject a second
 * script. Not because a second `init` would be ignored — the pixel merges keys on any later call, see
 * {@link identifyRedditVisitor} — but because re-initialising resets `bi.config` and mints a fresh
 * visitor id for somebody whose consent never changed.
 *
 * The script guard is separate from the latch on purpose: a withdrawal clears the latch (that is what
 * makes it a withdrawal), so a re-grant in the same visit takes this path again with the script already
 * in the head. The DOM is asked rather than trusted to the latch, exactly as `googleTags` does.
 *
 * @returns whether the pixel is running after this call
 */
export function startRedditPixel(
	pixelId: string | null, consent: ConsentState, matchKeys: RedditMatchKeys = {}): boolean {
	const scope = pixelWindow();
	if (scope === null) {
		return false;
	}
	if (running !== null) {
		return true;
	}
	if (pixelId === null || !consent.marketing || !allowedHere(scope)) {
		return false;
	}

	const rdt = rdtOf(scope);
	// Only the keys we actually have: an `undefined` passed through would be read as a value that
	// matches nobody rather than as an absent key.
	rdt("init", pixelId, {...definedKeys(matchKeys)});
	rdt("track", "PageVisit");

	if (scope.document.querySelector("script[data-reddit-pixel]") === null) {
		const script = scope.document.createElement("script");
		script.async = true;
		script.src = PIXEL_SCRIPT_SOURCE;
		script.setAttribute("data-reddit-pixel", "");
		scope.document.head.appendChild(script);
	}
	running = pixelId;
	viewedPath = scope.location.pathname;
	return true;
}

/**
 * Attach the account's match keys to a pixel that is ALREADY running (a login mid-session).
 *
 * `rdt('track', ...)` folds them into the running config, from the same build the header quotes:
 *
 *     case s: var x = String(e[1]), C = Kn(e[2]);
 *       if ("email" in C && (_ = Nn(C.email,"email"), delete C.email, L(_) && (bi.config.em = _, Y())),
 *
 * So a visitor who consented BEFORE signing in still gets matched, which is the common order: the banner
 * is answered on the landing page and the account arrives later.
 *
 * It sends a real `PageVisit`, so the caller must only reach it when the keys actually CHANGED — see
 * {@code analytics.syncRedditPixelWithConsent}. A no-op when nothing is running, like every export here.
 *
 * @returns whether the keys actually reached the pixel, so the caller can tell "already identified" from
 *          "not here" — remembering an identity that was never sent would lose it for the rest of the visit
 */
export function identifyRedditVisitor(matchKeys: RedditMatchKeys): boolean {
	const scope = pixelWindow();
	const keys = definedKeys(matchKeys);
	if (scope === null || running === null || Object.keys(keys).length === 0 || !allowedHere(scope)) {
		return false;
	}
	rdtOf(scope)("track", "PageVisit", keys);
	viewedPath = scope.location.pathname;
	return true;
}

/**
 * A route change. The pixel counts a view itself only when it starts, so every later one is explicit —
 * and every one is counted ONCE, because this asks {@link viewedPath} rather than trusting the caller
 * to know whether `init` or an identify already reported the page it is standing on.
 *
 * That also makes it safe on the load that started the pixel, which is why the hook needs no
 * "is this the first route" flag of its own.
 */
export function trackRedditPageVisit(): void {
	const scope = pixelWindow();
	if (scope === null || scope.location.pathname === viewedPath) {
		return;
	}
	send("PageVisit");
	if (running !== null && allowedHere(scope)) {
		viewedPath = scope.location.pathname;
	}
}

/** One purchase, as {@link trackRedditPurchase} reports it. */
export interface RedditPurchase {
	/**
	 * The dedup id the SERVER already reported this purchase under, taken from the api and never
	 * assembled here (kitecorp/transferify-api-service#1241). Reddit deduplicates on event name plus
	 * conversion id, so the browser's only job is to repeat the string verbatim: a value derived a second
	 * way would be counted as a second purchase rather than as the same one.
	 */
	conversionId: string;
	/** What was paid, in MAJOR units of {@link currency}. */
	value: number;
	/** ISO 4217, upper case — the spelling the server's own upload uses, so both reports agree. */
	currency: string;
}

/**
 * A completed purchase (kitecorp/transferify-api-service#1241).
 *
 * Sent through the same {@link send} every other event uses, so it inherits the two gates that make this
 * module's promise provable rather than argued: nothing leaves unless the pixel is RUNNING (which only
 * happens on granted advertising consent) and the current route is inside the scope
 * {@link redditPixelAllowedOn} defines. A caller therefore cannot report a purchase from a denied page by
 * forgetting to check, which matters here because two of the three purchase surfaces return through
 * `/activate/<token>` — a path that carries a single-use token and is deliberately outside the scope.
 *
 * Fires once per call. WHICH purchases are worth one call, and how a reload is stopped from repeating
 * one, is `./purchaseConversions`' decision; this function holds no memory of its own.
 *
 * @returns whether the event actually left, for the same reason {@link identifyRedditVisitor} answers
 *          that question: both gates can close between a caller deciding to report and this call, and a
 *          caller that recorded a purchase as reported when it was not would lose it permanently. A
 *          consent withdrawal or a navigation while the api read is in flight is exactly that window.
 */
export function trackRedditPurchase({conversionId, value, currency}: RedditPurchase): boolean {
	return send("Purchase", {conversionId, value, currency});
}

/**
 * Spend an owed `SignUp` on this page, if one is owed and this page may carry it (ui#834, api#1240).
 *
 * Called on every allowed page rather than at the moment of signup, because the two moments cannot be
 * the same one: signup completion lands on `/activate/<token>`, which {@link redditPixelAllowedOn}
 * denies because Reddit reports the full path and that path carries a single-use token. What is owed is
 * remembered by {@link ./redditSignUpMark}, which also explains why nothing is remembered at all when
 * advertising consent was not already granted.
 *
 * `conversionId` is the account's own uuid, RAW and unhashed — the SAME value the api writes as the
 * outbox row's conversion id (`RedditConversionRecorder.recordSignUp`). Reddit deduplicates on event
 * name plus conversion id, so the browser's report and the server's join into one conversion only while
 * these agree byte for byte. Anything else here would count every consenting signup twice.
 *
 * The mark is spent ONLY when the command actually reached the pixel, which is why this issues it
 * directly rather than through the shared `send` helper: `send` cannot report whether it did, and
 * clearing on a mere attempt would silently drop the signup of every visitor whose first page after
 * activation is one the pixel may not run on — the common case, not an edge one. {@link
 * identifyRedditVisitor} takes this same path for the same reason.
 *
 * Idempotent by construction across reloads and route changes: the mark is the permission, and it is
 * gone once used.
 *
 * @param accountId the account's uuid, or null/undefined when the session has none — nothing is sent
 *                  and, importantly, nothing is spent, so the event survives until the id arrives
 */
export function trackRedditSignUp(accountId: string | null | undefined): void {
	const scope = pixelWindow();
	if (scope === null || running === null || !allowedHere(scope)) {
		return;
	}
	if (!accountId || !redditSignUpPending()) {
		return;
	}
	rdtOf(scope)("track", "SignUp", {conversionId: accountId});
	clearRedditSignUpPending();
}

/**
 * Withdrawal: stop sending NOW rather than "next page load".
 *
 * The script cannot be unloaded, so the latch is dropped (every export above then no-ops) and the
 * identifiers the pixel wrote are expired. Dropping the latch alone would leave those cookies behind, and
 * expiring the cookies alone would leave the next event re-writing them.
 */
export function stopRedditPixel(): void {
	const scope = pixelWindow();
	if (scope === null || running === null) {
		return;
	}
	running = null;
	viewedPath = null;
	clearRedditCookies(scope.document);
}

/** @returns whether the command reached `rdt`, which is what a caller must not assume. */
function send(event: string, params?: Record<string, unknown>): boolean {
	const scope = pixelWindow();
	if (scope === null || running === null || !allowedHere(scope)) {
		return false;
	}
	if (params === undefined) {
		rdtOf(scope)("track", event);
	} else {
		rdtOf(scope)("track", event, params);
	}
	return true;
}

function definedKeys(matchKeys: RedditMatchKeys): Record<string, string> {
	return Object.fromEntries(
		Object.entries(matchKeys).filter((entry): entry is [string, string] => Boolean(entry[1])),
	);
}

/**
 * The cookie names {@link clearRedditCookies} deletes, as a PREFIX rule. Exported for the reason
 * `googleTags.GOOGLE_COOKIE_NAME_PATTERN` is: the Cookie Policy quotes `_rdt_*` names as published legal
 * copy, and `src/i18n/cookiePolicyIdentifiers.test.ts` holds them to this pattern (ui#1349).
 */
export const REDDIT_COOKIE_NAME_PATTERN = /^_rdt/;

/**
 * Delete the identifiers the Reddit pixel writes.
 *
 * `pixel.js` (build `rdt_c6181587`) sets four first-party cookies on the registrable domain, each for 90
 * days: `_rdt_uuid` (its own visitor id), `_rdt_cid` (the click identifier it read from the URL),
 * `_rdt_em` and `_rdt_pn` (the hashed match keys). Matched by {@link REDDIT_COOKIE_NAME_PATTERN} rather
 * than by that list, so a fifth one the pixel starts writing is removed too — the list is a fact about
 * somebody else's script and would go stale without anything noticing.
 *
 * Expiry is attempted on the host and on each parent domain for the reason `googleTags.clearGoogleCookies`
 * gives: the pixel writes on the registrable domain while this script may run on a subdomain, so expiring
 * only the exact host would leave the real cookie in place and look successful.
 */
function clearRedditCookies(document: Document): void {
	const names = document.cookie
		.split(";")
		.map((entry) => entry.split("=")[0]?.trim() ?? "")
		.filter((name) => REDDIT_COOKIE_NAME_PATTERN.test(name));
	const labels = document.location.hostname.split(".");
	const domains = labels
		.map((_, index) => labels.slice(index).join("."))
		.filter((domain) => domain.includes("."));
	names.forEach((name) => {
		document.cookie = `${name}=; Max-Age=0; path=/`;
		domains.forEach((domain) => {
			document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
			document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
		});
	});
}

/**
 * The advanced-matching keys for a signed-in account, or an empty set for a signed-out visitor (ui#834).
 *
 * Structural rather than typed against the `User` class on purpose: this module is React-free and has no
 * business importing the session entity, and what it needs is one string. It is here rather than in the
 * hook because it is a RULE — which of the account's fields may identify a person to an ad platform — and
 * rules do not live in React (epic #26).
 *
 * A blank address yields no key at all rather than an empty one: Reddit reads a present but empty match
 * key as a value that matches nobody, which is worse than sending none.
 */
export function redditMatchKeysFor(
	account: {email?: string | null; accountId?: string | null} | null | undefined): RedditMatchKeys {
	// Both fields are OPTIONAL in the parameter type, and that is not defensive clutter: `User` is
	// rehydrated from localStorage, so a session persisted before a field existed — and every signed-out
	// visitor's `new User({})` — carries `undefined` where the class declares a string. Reading `.trim()`
	// off it threw during hydration and replaced the whole app with react-router's error page, for every
	// visitor who was not signed in. Caught by the mocked battery, which is the only gate that renders.
	const email = account?.email?.trim() ?? "";
	// NOT trimmed, and not otherwise touched: this uuid has to stay byte-identical to the one the api
	// hashes, and the browser is echoing a value it did not mint. `account/accountId.ts` already decided
	// that anything but a non-empty string is no answer at all.
	const externalId = account?.accountId ?? "";
	const keys: RedditMatchKeys = {};
	if (email !== "") {
		keys.email = email;
	}
	if (externalId !== "") {
		keys.externalId = externalId;
	}
	return keys;
}
